import { cx } from '@linaria/core';
import { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react';
import * as timeago from 'timeago.js';
import { ChangesAlerts } from '../../../../types';
import { goToNewsFromModel, openProfile } from '../../../actions/nav';
import { AppStateType } from '../../../constants/enums';
import { locales } from '../../../locales/en';
import { isLevelMember } from '../../../mfcLogic/userProperties/level';
import { TransitionTypes } from '../../../state';
import { openRoom } from '../../../state/state.rooms';
import { routerPush } from '../../../utilities/router';
import Button from '../../Buttons/Button';
import { useAppStateSelector } from '../../Hooks/useAppState';
import { useModelColor } from '../../Hooks/useModelColor';
import { useUserSelector } from '../../Hooks/useUser';
import ModelStatusBadge from '../../common/ModelStatusBadge';
import Authentication from '../Authentication';
import { HeaderAlertSettings } from '../Modals/Drawers/HeaderAlerts';
import "./Alerts.linaria.module.css!=!./Alerts?./Alerts.linaria.module.css?css=H4sIAAAAAAAAA2WU3ZKjIBCF7/c9tmr3wpSYn03ch6EUGyS24CBBHWvffWuSmKHJ5al8BxvO6ewq33O356w92evtc/Uw+6xCrUwpwHhwf//92H0xaLluBqkOVK1D1TTaqMzbocxfMBAYUri23ts+4jvCdymPIH1EI6ExpZ1WbYR3I2fHT62m1QZwEu1UjsJZxA2AgjM7ftgirC3cvSzPf26/Xk+cFVW/D83aa5M9iWM+zBtxuXKPHeS4ipsbrSsHq+Onk4KzpT4YHBK5Xm+j13LJhDUejC/HoRKQ1eAnALPZ2y0cqtZ7Spn20I9JVpeRMz9POuDa6HHAaiklwmvesLzuO+nGt+S6aDlbDgeLMpEk6cP37RE4u9TBDjaRadosejLsOJPTbAIkkkbOisiCdDB8G+yRezTaH+RMjoUN0yosWleGyv3KshGENU3lluyr7b9j2sx6bgOhlQMwWS/FCxRnzq6nsLQfiVylNT4b9SeUzEG/8WrPP9RwLgqqVtQGtj6xXRE5xJn3VWjZiaro/Hx3pl9gMLNchESSb+S7c3GMXF81uSHMsn7VpEYrumiKiamw1FTFt9x/P7YYeS2NspKqBz1tW5NHh8Ms/LmmKj78FIVvOfNCXY8qkaSVRVLL2AFvjmctiamjpu7NdK8lsSC14JvlUcsi6eUcrrNlpGmNHqsaoaG1bHMuvdJYULX2lVPaPE8/D2S7Qe7nWj+3+1K8lrtoOOumP6Y/JHKtK9EpZ2+myR4jadOC0z5+mXjLu7ctfyxsTl8mtuCbxT3/bEnOS3vIa0kVTZmEHOOQ4lvEd8d/6/afDesGAAA="
const viewAllStyles = "atm_r3_1h6ojuz";
const rootStyles = "atm_lo_idpfg4 atm_le_idpfg4 atm_lk_idpfg4 atm_ll_idpfg4 atm_ks_15zigw atm_e2_1osqo2v";
const rootRowStyles = "atm_j6_12am3vd atm_9j_tlke0l";
const rowWrapperStyles = "atm_fc_1yb4nlp atm_h_1h6ojuz atm_9s_1txwivl";
const rowStyles = "atm_h_1h6ojuz atm_9s_1txwivl atm_vy_1osqo2v atm_lo_1y44olf atm_le_19bvopo atm_lk_1fwxnve atm_ll_1y44olf";
const statusStyles = "atm_7l_1fs2ovw";
const broadcastingStyles = "atm_7l_1nxixhv";
const textStyles = "atm_c8_1j6vyhq atm_g3_qgp822";
const embeddedTextStyles = "atm_c8_mavh16 atm_g3_1ex10cv";
const timeAgoStyles = "atm_9s_1ulexfb atm_c8_w1gvyb atm_7l_1fs2ovw";
const emptyStyles = "atm_cs_bfngof atm_c8_exct8b atm_r3_1h6ojuz atm_lo_1tcgj5g atm_le_1tcgj5g atm_lk_1tcgj5g atm_ll_1tcgj5g";
const emptyColorStyles = "atm_7l_1xvjxo1";
const avatarStyles = "atm_h0_ftgil2";
const viewAllButtonStyles = "atm_vy_1ef3xbi atm_2d_1kw7nm4 atm_lk_19bvopo atm_ll_19bvopo atm_lo_yh40bf atm_le_yh40bf";
interface RowProps {
  activeAlert: ChangesAlerts;
  embedded?: boolean;
}
const Row = (props: RowProps) => {
  const {
    activeAlert,
    embedded = false
  } = props;
  const {
    timestamp,
    key: type,
    id,
    news
  } = activeAlert;
  const slug = useUserSelector(id, 'slug');
  const name = useUserSelector(id, 'name') || 'loading';
  const color = useUserSelector(id, 'color');
  const level = useUserSelector(id, 'level');
  const isMember = isLevelMember(level);
  const modelColor = useModelColor(color);
  const onOpenAlert = useCallback(() => !isMember ? openRoom(id, {
    transitionSource: TransitionTypes.ROW
  }) : openProfile(id), [id, isMember]);
  const onOpenNews = useCallback(() => goToNewsFromModel(slug), [slug]);
  const renderStatus = type => <span className={cx(statusStyles, type === 'isBroadcasting' && broadcastingStyles)}>
            {news ? locales.alerts.newsUpdateVerbose : locales.alerts[type]}
        </span>;
  if (!slug) {
    return null;
  }
  return <div className={rootRowStyles}>
            <div className={rowWrapperStyles}>
                <div onClick={news ? onOpenNews : onOpenAlert} data-testid="Alert__Results" className={rowStyles}>
                    <ModelStatusBadge id={id} alt={name} size={embedded ? 32 : 40} kind={type} news={news} addBoxShadow className={avatarStyles} />
                    <div data-testid="AlertRow__UserStatus" className={embedded ? embeddedTextStyles : textStyles}>
                        <span style={{
            color: modelColor
          }}>{name}</span>{' '}
                        {renderStatus(type)}
                        <span className={timeAgoStyles}>
                            {timeago.format(timestamp)}
                        </span>
                    </div>
                </div>
            </div>
        </div>;
};
export interface AlertProps {
  alerts: ChangesAlerts[];
  embedded?: boolean;
  query?: string;
  clearAlerts(): void;
  onAlerts?(): void;
  onViewAllHistory?(): void;
}
const Alerts = (props: AlertProps) => {
  const {
    alerts,
    embedded = false,
    onAlerts = () => {},
    onViewAllHistory = () => {},
    query,
    clearAlerts
  } = props;
  const isAuthenticated = useAppStateSelector(AppStateType.APP, 'authenticated');
  const [time, setTime] = useState(Date.now());

  // Force re-render every 30 seconds to update timeago
  useEffect(() => {
    const timeout = setTimeout(() => setTime(Date.now()), 30000);
    return () => {
      clearTimeout(timeout);
    };
  }, [time]);
  useEffect(() => {
    onAlerts();
  }, [alerts]);
  const alertRows = useMemo(() => !query ? alerts : alerts.filter(alert => alert.name?.toLocaleLowerCase().includes(query.toLocaleLowerCase())), [alerts, query]);
  const handleViewAllHistory = useCallback(() => {
    routerPush('/friends?view=alerts');
    onViewAllHistory();
  }, [onViewAllHistory]);
  if (!isAuthenticated) {
    return <Authentication subject={locales.alerts.see_alerts} />;
  }
  return <>
            <div data-testid="Alerts__Modal" id="list-section" className={cx('no-scrollbars', rootStyles)}>
                {!embedded && <HeaderAlertSettings clearAlerts={clearAlerts} />}
                {alertRows.length > 0 ? <Fragment>
                        {alertRows.map(alert => {
          const {
            id
          } = alert;
          return <Row key={id} activeAlert={alert} embedded={embedded} />;
        })}
                    </Fragment> : query ? <div className={cx(emptyStyles, emptyColorStyles)}>
                        {locales.alerts.no_search_results}{' '}
                        <strong>{query}</strong>
                    </div> : <div className={cx(emptyStyles, emptyColorStyles)}>
                        {locales.alerts.no_history}
                    </div>}
            </div>
            {embedded && <div className={viewAllStyles}>
                    <Button size={'small'} outlined className={viewAllButtonStyles} onClick={handleViewAllHistory}>
                        View All History
                    </Button>
                </div>}
        </>;
};
export default memo(Alerts);